import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  Injectable,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { PreloadingStrategy, provideRouter, Route, Router, withPreloading } from '@angular/router';
import { initDaisiApp } from '@daisi/init';
import { LocaleService } from '@daisi/services/locale/locale.service';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { provideOAuthClient } from 'angular-oauth2-oidc';

import { routes } from './app.routes';
import { mapSnapshotPath, timelineSnapshotPath } from '@daisi/app.paths';
import { NdwAuthService, provideNdwAuth } from '@ndwnu/auth';
import { authConfig } from '@daisi/auth.config';
import { UserStateService } from '@daisi/services/user-state/user-state.service';
import { filter, Observable, of, take } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { OauthInterceptor } from '@services/auth/oauth-interceptor';
import { RoadOperatorService } from '@services/road-operator/road-operator.service';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@env/environment';

/**
 * The snapshot pages are only ever loaded by Gotenberg's headless Chromium, to be photographed for
 * the notification mail.
 */
function isSnapshotPage(): boolean {
  return [timelineSnapshotPath, mapSnapshotPath].some((path) =>
    location.pathname.startsWith(`/${path}/`),
  );
}

/**
 * Preloads every lazy route, except on the snapshot pages: Gotenberg never navigates away from the
 * page it opened, so fetching the chunks of the rest of the app would only slow the render down.
 */
@Injectable({ providedIn: 'root' })
class PreloadAllExceptOnSnapshotPages implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return isSnapshotPage() ? of(null) : load();
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideNdwAuth(authConfig),
    provideZonelessChangeDetection(),
    provideRouter(routes, withPreloading(PreloadAllExceptOnSnapshotPages)),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideOAuthClient(),
    {
      provide: LOCALE_ID,
      useFactory: (localeService: LocaleService) => localeService.getLocale(),
      deps: [LocaleService],
    },
    provideAppInitializer(() => {
      const initializerFn = initDaisiApp(inject(LocaleService));
      return initializerFn();
    }),
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Force early instantiation of TraceService
      inject(TraceService);
    }),
    provideAppInitializer(() => {
      const ndwAuthService = inject(NdwAuthService);
      const roadOperatorservice = inject(RoadOperatorService);

      return ndwAuthService.isDoneLoading$
        .pipe(
          filter((isDoneLoading) => isDoneLoading),
          take(1),
        )
        .pipe(
          mergeMap(() => ndwAuthService.isAuthenticated$),
          take(1),
        )
        .pipe(
          mergeMap((authenticated) => {
            if (authenticated) {
              return roadOperatorservice.initialize();
            }
            return of([]);
          }),
          catchError((error) => {
            return of([]);
          }),
        );
    }),
    provideAppInitializer(() => {
      const ndwAuthService = inject(NdwAuthService);
      const userStateService = inject(UserStateService);

      return ndwAuthService.isDoneLoading$
        .pipe(
          filter((isDoneLoading) => isDoneLoading),
          take(1),
        )
        .pipe(
          mergeMap(() => ndwAuthService.isAuthenticated$),
          take(1),
        )
        .pipe(
          mergeMap((authenticated) => {
            if (authenticated) {
              return userStateService.initialize();
            }
            return of([]);
          }),
          catchError((error) => {
            return of([]);
          }),
        );
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OauthInterceptor,
      multi: true,
    },
    provideServiceWorker('custom-service-worker.js', {
      // Never on the snapshot pages. Those are only ever loaded by Gotenberg's
      // headless Chromium, which reuses its browser instances, so a worker registered during one
      // render survives into the next. It then re-issues the page's XHR from its own context,
      // which the Authorization header Gotenberg sets over CDP on the page session does not
      // reach: the call comes back 401, the chart never draws and readyForSnapshotGeneration is
      // never set, so every render after the first one in a given instance times out. Nothing on
      // those pages benefits from offline caching or push anyway.
      enabled: environment.production && !isSnapshotPage(),
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
